import React, { useState, useMemo, useEffect } from 'react';
import { Trial } from '../types';
import { ChevronDown, ChevronUp, Search, ArrowUpDown, ExternalLink } from 'lucide-react';
import { getExtraEndpointsForIndication } from '../constants';
import { formatG35AeCaption } from '../utils/g35Ae';

interface TrialTableProps {
  data: Trial[];
  selectedIds: string[];
  onToggleSelection: (id: string) => void;
  indicationName?: string;
  statusFilter: 'All' | Trial['preferredStatus'];
  guidelineFilter: 'nccn' | 'non-nccn';
  /** Nested NCCN regimen: same table + detail panel, no toolbar/selection/status/pagination */
  compact?: boolean;
}

const NCCN_STATUS_EMPTY_NOTE: Record<Trial['preferredStatus'], string> = {
  Preferred: 'No Preferred data available.',
  'Other Recommended': 'No Other Recommended data available.',
  'Useful in Certain Circumstances': 'No Useful in Certain Circumstances data available.',
  'N/A': 'No N/A status data available.',
};

const TrialTable: React.FC<TrialTableProps> = ({
  data,
  selectedIds,
  onToggleSelection,
  indicationName,
  statusFilter,
  guidelineFilter,
  compact = false,
}) => {
  const extraEndpoints = useMemo(() => getExtraEndpointsForIndication(indicationName), [indicationName]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{key: keyof Trial, direction: 'asc' | 'desc'} | null>(null);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const itemsPerPage = 20;

  const normalizeDetailValue = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === 'null') return null;
    return text;
  };

  const formatKeyLabel = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

  const formatStructuredText = (value: unknown): string[] | null => {
    const normalized = normalizeDetailValue(value);
    if (!normalized) return null;
    const trimmed = normalized.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const entries = parsed
            .map((entry) => normalizeDetailValue(entry))
            .filter((entry): entry is string => Boolean(entry));
          return entries.length > 0 ? entries : null;
        }
        if (parsed && typeof parsed === 'object') {
          const entries = Object.entries(parsed)
            .map(([key, val]) => {
              if (val === null || val === undefined) return null;
              if (typeof val === 'string' && (!val.trim() || val.trim().toLowerCase() === 'null')) return null;
              if (Array.isArray(val)) {
                const compact = val.map((item) => normalizeDetailValue(item)).filter(Boolean);
                if (compact.length === 0) return null;
                return `${formatKeyLabel(key)}: ${compact.join(', ')}`;
              }
              if (typeof val === 'object') {
                const inner = JSON.stringify(val);
                return inner && inner !== '{}' ? `${formatKeyLabel(key)}: ${inner}` : null;
              }
              return `${formatKeyLabel(key)}: ${String(val)}`;
            })
            .filter((entry): entry is string => Boolean(entry));
          return entries.length > 0 ? entries : null;
        }
      } catch (e) {
        return [normalized];
      }
    }
    return [normalized];
  };

  const renderDetailValue = (value: unknown) => {
    const lines = formatStructuredText(value);
    if (!lines || lines.length === 0) return null;
    if (lines.length === 1) {
      return <span className="whitespace-pre-wrap break-words">{lines[0]}</span>;
    }
    return (
      <ul className="list-disc pl-4 space-y-1 whitespace-pre-wrap break-words">
        {lines.map((line, index) => (
          <li key={`${line}-${index}`}>{line}</li>
        ))}
      </ul>
    );
  };

  const formatPercentPair = (exp?: number | null, ctrl?: number | null) => {
    const expVal = exp !== null && exp !== undefined ? `${exp}%` : 'N/A';
    const ctrlVal = ctrl !== null && ctrl !== undefined ? `${ctrl}%` : 'N/A';
    if (expVal === 'N/A' && ctrlVal === 'N/A') {
      return null;
    }
    return `${expVal} / ${ctrlVal}`;
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev => 
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSort = (key: keyof Trial) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredData = React.useMemo(() => {
    let sortableItems = [...data];
    if (!compact) {
      if (guidelineFilter === 'nccn') {
        sortableItems = sortableItems.filter((item) => item.preferredStatus !== 'N/A');
      } else if (guidelineFilter === 'non-nccn') {
        sortableItems = sortableItems.filter((item) => item.preferredStatus === 'N/A');
      }
      const effectiveStatus = guidelineFilter === 'non-nccn' ? 'All' : statusFilter;
      if (searchTerm) {
        sortableItems = sortableItems.filter(
          (item) =>
            item.trialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.expDrug.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }
      if (effectiveStatus !== 'All') {
        sortableItems = sortableItems.filter((item) => item.preferredStatus === effectiveStatus);
      }
    }

    const trialPmidKey = (trial: Trial) =>
      (trial.pmid || '').trim() ||
      String(trial.id || '').match(/(\d{5,})/)?.[1] ||
      `id:${trial.id}`;

    // Compact (NCCN regimen) view: preserve caller order so same-PMID arms stay together.
    if (compact && sortConfig === null) {
      return sortableItems;
    }

    // Default sort: by ORR exp value (descending), then by other numerical exp values
    if (sortConfig === null) {
      const statusRank: Record<string, number> = {
        'Preferred': 0,
        'Useful in Certain Circumstances': 1,
        'Other Recommended': 2,
        'N/A': 3,
      };
      sortableItems.sort((a, b) => {
        const rankA = statusRank[a.preferredStatus] ?? 99;
        const rankB = statusRank[b.preferredStatus] ?? 99;
        if (rankA !== rankB) {
          return rankA - rankB;
        }
        // Primary sort: ORR exp (descending)
        const orrA = a.orrExp ?? -Infinity;
        const orrB = b.orrExp ?? -Infinity;
        if (orrA !== orrB) {
          return orrB - orrA; // Descending
        }
        // Secondary sort: mPFS exp (descending)
        const pfsA = a.mPFSExp ?? -Infinity;
        const pfsB = b.mPFSExp ?? -Infinity;
        if (pfsA !== pfsB) {
          return pfsB - pfsA; // Descending
        }
        // Tertiary sort: mOS exp (descending)
        const osA = a.mOSExp ?? -Infinity;
        const osB = b.mOSExp ?? -Infinity;
        if (osA !== osB) {
          return osB - osA; // Descending
        }
        return 0;
      });
    } else {
      // Manual sort when user clicks column header
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Keep same-PMID arms contiguous after any sort in compact regimen tables.
    if (compact) {
      const byPmid = new Map<string, Trial[]>();
      const pmidOrder: string[] = [];
      for (const trial of sortableItems) {
        const key = trialPmidKey(trial);
        if (!byPmid.has(key)) {
          byPmid.set(key, []);
          pmidOrder.push(key);
        }
        byPmid.get(key)!.push(trial);
      }
      return pmidOrder.flatMap((key) => byPmid.get(key)!);
    }

    return sortableItems;
  }, [compact, data, searchTerm, sortConfig, statusFilter, guidelineFilter]);

  const selectedTrials = React.useMemo(
    () =>
      selectedIds
        .map((id) => data.find((trial) => trial.id === id))
        .filter((trial): trial is Trial => trial !== undefined),
    [data, selectedIds]
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = compact ? filteredData : filteredData.slice(startIndex, endIndex);

  // Reset to page 1 when search or NCCN status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, guidelineFilter]);

  const tableColSpan = (compact ? 9 : 11) + extraEndpoints.length;

  const StatusBadge = ({ status }: { status: string }) => {
    const styles = {
        'Preferred': 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
        'Other Recommended': 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
        'Useful in Certain Circumstances': 'bg-sky-50 text-sky-700 ring-1 ring-sky-600/20',
        'N/A': 'bg-slate-50 text-slate-500 ring-1 ring-slate-400/20'
    };
    const style = styles[status as keyof typeof styles] || styles['N/A'];
    
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${style}`}>
            {status}
        </span>
    );
  };

  const Sparkline = ({ val1, val2, status }: { val1: number | null, val2: number | null, status: Trial['preferredStatus'] }) => {
    if (val1 === null && val2 === null) return <span className="text-gray-300">-</span>;
    const max = Math.max(val1 || 0, val2 || 0, 100);
    const w1 = ((val1 || 0) / max) * 100;
    const w2 = ((val2 || 0) / max) * 100;
    const barColor = {
      'Preferred': 'bg-emerald-500',
      'Other Recommended': 'bg-amber-500',
      'Useful in Certain Circumstances': 'bg-sky-500',
      'N/A': 'bg-slate-300',
    }[status] || 'bg-slate-300';
    
    return (
        <div className="flex flex-col gap-1 w-20">
            <div className={`h-1.5 ${barColor} rounded-full opacity-90`} style={{ width: `${w1}%` }}></div>
            <div className="h-1.5 bg-slate-300 rounded-full opacity-60" style={{ width: `${w2}%` }}></div>
        </div>
    );
  };

  const formatNumber = (value?: number | null, suffix = '') => {
    if (value === null || value === undefined) return 'N/A';
    return `${value}${suffix}`;
  };

  const formatCompareValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (Array.isArray(value)) {
      const joined = value.map((item) => String(item)).filter(Boolean).join(', ');
      return joined || 'N/A';
    }
    return String(value);
  };

  const compareAttributes: Array<{ key: string; label: string; get: (trial: Trial) => unknown }> = useMemo(() => {
    const base = [
      { key: 'expDrug', label: 'Exp Drug', get: (t: Trial) => t.expDrug },
      { key: 'ctrlDrug', label: 'Ctrl Drug', get: (t: Trial) => t.ctrlDrug },
      { key: 'expN', label: 'Exp N', get: (t: Trial) => t.expN },
      { key: 'ctrlN', label: 'Ctrl N', get: (t: Trial) => t.ctrlN },
      { key: 'orrExp', label: 'ORR Exp (%)', get: (t: Trial) => t.orrExp },
      { key: 'orrCtrl', label: 'ORR Ctrl (%)', get: (t: Trial) => t.orrCtrl },
      { key: 'crExp', label: 'CR Exp (%)', get: (t: Trial) => t.crExp },
      { key: 'crCtrl', label: 'CR Ctrl (%)', get: (t: Trial) => t.crCtrl },
      { key: 'mPFSExp', label: 'mPFS Exp (mos)', get: (t: Trial) => t.mPFSExp },
      { key: 'mPFSCtrl', label: 'mPFS Ctrl (mos)', get: (t: Trial) => t.mPFSCtrl },
      { key: 'pfsHR', label: 'PFS HR', get: (t: Trial) => t.pfsHR },
      { key: 'pfsHRCI', label: 'PFS HR 95% CI', get: (t: Trial) => t.pfsHRCI },
      { key: 'mOSExp', label: 'mOS Exp (mos)', get: (t: Trial) => t.mOSExp },
      { key: 'mOSCtrl', label: 'mOS Ctrl (mos)', get: (t: Trial) => t.mOSCtrl },
      { key: 'osHR', label: 'OS HR', get: (t: Trial) => t.osHR },
      { key: 'osHRCI', label: 'OS HR 95% CI', get: (t: Trial) => t.osHRCI },
      { key: 'g35AEExp', label: 'G3-5 AEs Exp', get: (t: Trial) => t.g35AEExp },
      { key: 'g35AECtrl', label: 'G3-5 AEs Ctrl', get: (t: Trial) => t.g35AECtrl },
      { key: 'g5DeathExp', label: 'G5/Death Exp', get: (t: Trial) => t.g5DeathExp },
      { key: 'g5DeathCtrl', label: 'G5/Death Ctrl', get: (t: Trial) => t.g5DeathCtrl },
      { key: 'trialArmNotes', label: 'Trial Arm Notes', get: (t: Trial) => t.trialArmNotes },
      { key: 'indicationDetails', label: 'Indication Details', get: (t: Trial) => t.indicationDetails },
      { key: 'primaryEndpoint', label: 'Primary Endpoint', get: (t: Trial) => t.primaryEndpoint },
      { key: 'secondaryEndpoints', label: 'Secondary Endpoints', get: (t: Trial) => t.secondaryEndpoints },
      { key: 'medianFollowUpTimeMonths', label: 'Median Follow-up (mos)', get: (t: Trial) => t.medianFollowUpTimeMonths },
      { key: 'baselineCharacteristics', label: 'Baseline Characteristics', get: (t: Trial) => t.baselineCharacteristics },
      { key: 'biomarkersInfo', label: 'Biomarkers Info', get: (t: Trial) => t.biomarkersInfo },
      { key: 'experimentalRegimen', label: 'Experimental Regimen', get: (t: Trial) => t.experimentalRegimen },
      { key: 'controlRegimen', label: 'Control Regimen', get: (t: Trial) => t.controlRegimen },
      { key: 'otherEfficacy', label: 'Other Efficacy', get: (t: Trial) => t.otherEfficacy },
      { key: 'otherInfoAboutAEs', label: 'Other Info About AEs', get: (t: Trial) => t.otherInfoAboutAEs },
      { key: 'phase', label: 'Phase', get: (t: Trial) => t.phase },
      { key: 'trialName', label: 'Trial Name', get: (t: Trial) => t.trialName },
      { key: 'nctId', label: 'NCT ID', get: (t: Trial) => t.nctId },
      { key: 'doi', label: 'DOI', get: (t: Trial) => t.doi },
      { key: 'pmid', label: 'PMID', get: (t: Trial) => t.pmid },
    ];
    const extra = extraEndpoints.flatMap((ep) => [
      { key: ep.expKey as string, label: `${ep.label} Exp (%)`, get: (t: Trial) => (t as any)[ep.expKey] },
      { key: ep.ctrlKey as string, label: `${ep.label} Ctrl (%)`, get: (t: Trial) => (t as any)[ep.ctrlKey] },
    ]);
    return [...base, ...extra];
  }, [extraEndpoints]);


  return (
    <div
      className={
        compact
          ? 'overflow-hidden'
          : 'bg-white rounded-[2rem] shadow-card border border-gray-100 overflow-hidden flex flex-col'
      }
    >
        {!compact && (
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between gap-4 items-center bg-white">
            <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative group w-full sm:w-72">
                    <input 
                        type="text" 
                        placeholder="Search trials, drugs..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2.5 text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none w-full transition-all text-slate-600 placeholder:text-slate-400"
                    />
                    <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                </div>
            </div>
            
            <div className="flex items-center gap-4">
                 {selectedIds.length > 0 && (
                    <button
                      onClick={() => setIsCompareOpen(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 animate-fadeIn"
                    >
                        Compare ({selectedIds.length})
                    </button>
                 )}
            </div>
        </div>
        )}

        {/* Table Container */}
        <div className={`overflow-x-auto overflow-y-auto custom-scrollbar ${compact ? '' : 'max-h-[70vh]'}`}>
            <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-slate-100 sticky top-0 z-20">
                    <tr>
                        {!compact && (
                        <th scope="col" className="w-12 px-3 py-2 text-left sticky top-0 left-0 z-30 bg-slate-100">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 bg-white cursor-pointer" />
                        </th>
                        )}
                        <th
                          scope="col"
                          className={`px-3 py-2 text-left text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors sticky top-0 z-20 bg-slate-100 ${compact ? 'left-0' : 'left-12'}`}
                          onClick={() => handleSort('trialName')}
                        >
                            <div className="flex items-center gap-1">Trial <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        </th>
                        {!compact && (
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                        )}
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-bold text-slate-400 uppercase tracking-widest">Regimen (Exp / Ctrl)</th>
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-bold text-slate-400 uppercase tracking-widest min-w-[140px] max-w-[220px]">
                          Trial arm notes
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-center text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors"
                          onClick={() => handleSort('orrExp')}
                        >
                          <div className="flex items-center justify-center gap-1">ORR <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-center text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors"
                          onClick={() => handleSort('crExp')}
                        >
                          <div className="flex items-center justify-center gap-1">CR <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-center text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors"
                          onClick={() => handleSort('mPFSExp')}
                        >
                          <div className="flex items-center justify-center gap-1">PFS (m) <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-center text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors"
                          onClick={() => handleSort('mOSExp')}
                        >
                          <div className="flex items-center justify-center gap-1">OS (m) <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-center text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors"
                          onClick={() => handleSort('g35AEExp')}
                        >
                          <div className="flex items-center justify-center gap-1">G3-5 AEs <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                        </th>
                        {extraEndpoints.map((ep) => (
                          <th
                            key={ep.id}
                            scope="col"
                            className="px-3 py-2 text-center text-[11px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 group transition-colors"
                            onClick={() => handleSort(ep.expKey)}
                          >
                            <div className="flex items-center justify-center gap-1">{ep.label} <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                          </th>
                        ))}
                        <th scope="col" className="relative px-3 py-2">
                            <span className="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                    {filteredData.length === 0 ? (
                        <tr>
                            <td
                                colSpan={tableColSpan}
                                className="px-6 py-14 text-center text-sm text-slate-600"
                            >
                                {statusFilter !== 'All' ? (
                                    <p className="max-w-md mx-auto">
                                        {NCCN_STATUS_EMPTY_NOTE[statusFilter]}
                                    </p>
                                ) : searchTerm.trim() ? (
                                    <p className="max-w-md mx-auto">No trials match your search.</p>
                                ) : (
                                    <p className="max-w-md mx-auto">No trial data to display.</p>
                                )}
                            </td>
                        </tr>
                    ) : (
                    paginatedData.map((trial, rowIndex) => {
                        const trialPmid =
                          (trial.pmid || '').trim() ||
                          String(trial.id || '').match(/(\d{5,})/)?.[1] ||
                          '';
                        const prevPmid =
                          rowIndex > 0
                            ? (paginatedData[rowIndex - 1].pmid || '').trim() ||
                              String(paginatedData[rowIndex - 1].id || '').match(/(\d{5,})/)?.[1] ||
                              ''
                            : '';
                        const startsPmidGroup =
                          compact && Boolean(trialPmid) && trialPmid !== prevPmid;
                        const pmidArmCount = compact && trialPmid
                          ? paginatedData.filter((t) => {
                              const p =
                                (t.pmid || '').trim() ||
                                String(t.id || '').match(/(\d{5,})/)?.[1] ||
                                '';
                              return p === trialPmid;
                            }).length
                          : 0;

                        return (
                        <React.Fragment key={trial.id}>
                            {startsPmidGroup && pmidArmCount > 1 && (
                              <tr className="bg-slate-100/90">
                                <td
                                  colSpan={tableColSpan}
                                  className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 border-t-2 border-slate-200"
                                >
                                  PMID {trialPmid}
                                  <span className="ml-1.5 font-medium normal-case tracking-normal text-slate-400">
                                    · {pmidArmCount} arms
                                  </span>
                                </td>
                              </tr>
                            )}
                            <tr
                                onClick={() => toggleRow(trial.id)}
                                className={`group transition-all duration-200 cursor-pointer ${
                                  selectedIds.includes(trial.id) ? 'bg-indigo-50/40' : 'hover:bg-slate-50/80'
                                } ${startsPmidGroup && pmidArmCount <= 1 ? 'border-t border-slate-100' : ''}`}
                            >
                                {!compact && (
                                <td className="px-3 py-2 whitespace-nowrap sticky left-0 z-10 bg-white">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedIds.includes(trial.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => onToggleSelection(trial.id)}
                                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer bg-white" 
                                    />
                                </td>
                                )}
                                <td className={`px-3 py-2 sticky z-10 bg-white ${compact ? 'left-0' : 'left-12'}`}>
                                    <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors cursor-pointer whitespace-normal break-words leading-snug">{trial.trialName}</div>
                                    <div className="text-xs text-slate-400 font-medium mt-0.5">n={trial.expN} vs {trial.ctrlN}</div>
                                    {trial.id && trial.id !== 'unknown' && (
                                      <div
                                        className="text-[10px] text-slate-400 font-mono mt-0.5 break-all leading-tight"
                                        title="ruby_data identifier"
                                      >
                                        {trial.id}
                                      </div>
                                    )}
                                </td>
                                {!compact && (
                                <td className="px-3 py-2 align-top max-w-[220px]">
                                    <StatusBadge status={trial.preferredStatus} />
                                    {trial.nccnNotes?.trim() ? (
                                      <div className="text-[10px] text-slate-400 mt-1 font-medium whitespace-normal break-words leading-snug">
                                        {trial.nccnNotes}
                                      </div>
                                    ) : null}
                                </td>
                                )}
                                <td className="px-3 py-2 align-top">
                                    <div className="flex flex-col gap-0.5">
                                        <div className="text-sm font-medium text-slate-700 whitespace-normal break-words leading-snug">{trial.expDrug}</div>
                                        <div className="text-xs text-slate-400 whitespace-normal break-words leading-snug">vs. {trial.ctrlDrug}</div>
                                    </div>
                                </td>
                                <td className="px-3 py-2 align-top min-w-[140px] max-w-[220px]">
                                    {trial.trialArmNotes?.trim() ? (
                                      <div
                                        className="text-xs text-slate-600 whitespace-normal break-words leading-snug line-clamp-4"
                                        title={trial.trialArmNotes}
                                      >
                                        {trial.trialArmNotes}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-400">N/A</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <div className="text-sm text-slate-700 font-bold font-mono">{trial.orrExp !== null ? `${trial.orrExp}%` : 'N/A'} <span className="text-slate-400 font-normal">/ {trial.orrCtrl !== null ? `${trial.orrCtrl}%` : 'N/A'}</span></div>
                                        <Sparkline val1={trial.orrExp} val2={trial.orrCtrl} status={trial.preferredStatus} />
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <div className="text-sm text-slate-700 font-bold font-mono">{trial.crExp !== null ? `${trial.crExp}%` : 'N/A'} <span className="text-slate-400 font-normal">/ {trial.crCtrl !== null ? `${trial.crCtrl}%` : 'N/A'}</span></div>
                                        <Sparkline val1={trial.crExp} val2={trial.crCtrl} status={trial.preferredStatus} />
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="text-sm text-slate-700 font-medium tabular-nums">{trial.mPFSExp} / {trial.mPFSCtrl}</div>
                                        {trial.pfsHR && <div className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5 font-semibold">HR: {trial.pfsHR}</div>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="text-sm text-slate-700 font-medium tabular-nums">{trial.mOSExp} / {trial.mOSCtrl}</div>
                                        {trial.osHR && <div className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5 font-semibold">HR: {trial.osHR}</div>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="text-sm text-slate-600 font-medium tabular-nums">
                                      {trial.g35AEExp !== null ? `${trial.g35AEExp}%` : 'N/A'}
                                      <span className="text-slate-400 font-normal">
                                        {trial.g35AECtrl !== null ? ` / ${trial.g35AECtrl}%` : ''}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-slate-400 mt-0.5">{formatG35AeCaption(trial.g35AESource)}</div>
                                </td>
                                {extraEndpoints.map((ep) => {
                                  const expVal = (trial as any)[ep.expKey];
                                  const ctrlVal = (trial as any)[ep.ctrlKey];
                                  return (
                                    <td key={ep.id} className="px-3 py-2 whitespace-nowrap text-center">
                                      <div className="flex flex-col items-center justify-center gap-1">
                                        <div className="text-sm text-slate-700 font-bold font-mono">
                                          {expVal !== null && expVal !== undefined ? `${expVal}%` : 'N/A'}
                                          <span className="text-slate-400 font-normal">
                                            {' '}/ {ctrlVal !== null && ctrlVal !== undefined ? `${ctrlVal}%` : 'N/A'}
                                          </span>
                                        </div>
                                        <Sparkline val1={typeof expVal === 'number' ? expVal : null} val2={typeof ctrlVal === 'number' ? ctrlVal : null} status={trial.preferredStatus} />
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                                    <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRow(trial.id);
                                        }}
                                        className={`p-2 rounded-full hover:bg-white hover:shadow-sm transition-all ${expandedRows.includes(trial.id) ? 'bg-indigo-50 text-indigo-600' : 'text-slate-400'}`}
                                    >
                                        {expandedRows.includes(trial.id) ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                    </button>
                                </td>
                            </tr>
                            {expandedRows.includes(trial.id) && (
                                <tr className="bg-slate-50/50 border-b border-gray-100">
                                    <td colSpan={10 + extraEndpoints.length} className="px-6 py-6">
                                        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                                            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
                                                <h4 className="font-bold text-slate-800 text-sm">Detailed Analysis: {trial.trialName}</h4>
                                                {trial.pmid ? (
                                                    <a
                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${trial.pmid}/`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 text-xs font-semibold bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
                                                    >
                                                        PubMed Source <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-400 text-xs font-semibold bg-slate-100 px-3 py-1.5 rounded-lg">
                                                        PubMed Source Unavailable
                                                    </span>
                                                )}
                                            </div>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-sm">
                                            <div className="space-y-3">
                                                <h5 className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Trial Details</h5>
                                                <div className="text-xs text-slate-500 space-y-1">
                                                    <p className="break-words"><span className="font-medium text-slate-700">Trial Arm Notes:</span> {trial.trialArmNotes || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Phase:</span> {trial.phase || 'N/A'}</p>
                                                    <p className="break-words">
                                                      <span className="font-medium text-slate-700">ruby_data identifier:</span>{' '}
                                                      <span className="font-mono">{trial.id || 'N/A'}</span>
                                                    </p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">NCT ID:</span> {trial.nctId || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">PMID:</span> {trial.pmid || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">DOI:</span> {trial.doi || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Publication Date:</span> {trial.publicationDate}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Median Follow-up (mos):</span> {trial.medianFollowUpTimeMonths ?? 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Indication Details:</span> {trial.indicationDetails || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Primary Endpoint:</span> {trial.primaryEndpoint || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Secondary Endpoints:</span> {trial.secondaryEndpoints || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Experimental Regimen:</span> {renderDetailValue(trial.experimentalRegimen) || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Control Regimen:</span> {renderDetailValue(trial.controlRegimen) || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                <h5 className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Baseline & Regimen</h5>
                                                <div className="text-xs text-slate-500 space-y-2">
                                                    <p className="break-words"><span className="font-medium text-slate-700">Baseline Characteristics:</span> {renderDetailValue(trial.baselineCharacteristics) || 'N/A'}</p>
                                                    <p className="break-words"><span className="font-medium text-slate-700">Biomarkers Info:</span> {renderDetailValue(trial.biomarkersInfo) || 'N/A'}</p>
                                                    {renderDetailValue(trial.otherEfficacy) && (
                                                      <div className="break-words">
                                                        <span className="font-medium text-slate-700">Other Efficacy:</span>{' '}
                                                        {renderDetailValue(trial.otherEfficacy)}
                                                      </div>
                                                    )}
                                                    {renderDetailValue(trial.otherInfoAboutAEs) && (
                                                      <div className="break-words">
                                                        <span className="font-medium text-slate-700">Other Info About AEs:</span>{' '}
                                                        {renderDetailValue(trial.otherInfoAboutAEs)}
                                                      </div>
                                                    )}
                                                    {formatPercentPair(trial.g34AEExp, trial.g34AECtrl) && (
                                                      <p className="break-words">
                                                        <span className="font-medium text-slate-700">G3/4 AEs:</span>{' '}
                                                        {formatPercentPair(trial.g34AEExp, trial.g34AECtrl)}
                                                      </p>
                                                    )}
                                                    {formatPercentPair(trial.g35AEExp, trial.g35AECtrl) && (
                                                      <p className="break-words">
                                                        <span className="font-medium text-slate-700">
                                                          {formatG35AeCaption(trial.g35AESource)}:
                                                        </span>{' '}
                                                        {formatPercentPair(trial.g35AEExp, trial.g35AECtrl)}
                                                      </p>
                                                    )}
                                                    {formatPercentPair(trial.g5DeathExp, trial.g5DeathCtrl) && (
                                                      <p className="break-words">
                                                        <span className="font-medium text-slate-700">G5 / Death:</span>{' '}
                                                        {formatPercentPair(trial.g5DeathExp, trial.g5DeathCtrl)}
                                                      </p>
                                                    )}
                                                    {formatPercentPair(trial.dropOutExp, trial.dropOutCtrl) && (
                                                      <p className="break-words">
                                                        <span className="font-medium text-slate-700">Dropout Rate:</span>{' '}
                                                        {formatPercentPair(trial.dropOutExp, trial.dropOutCtrl)}
                                                      </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                        );
                    })
                    )}
                </tbody>
            </table>
        </div>
        
        {/* Pagination */}
        {!compact && totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-100 bg-white">
                <p className="text-xs text-slate-500 font-medium">
                    Showing <span className="text-slate-900">{startIndex + 1}</span> to <span className="text-slate-900">{Math.min(endIndex, filteredData.length)}</span> of <span className="text-slate-900">{filteredData.length}</span> entries
                </p>
                <div className="flex gap-2">
                    <button 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Previous
                    </button>
                    <div className="flex gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            // Show first page, last page, current page, and pages around current
                            if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                            ) {
                                return (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`w-7 h-7 flex items-center justify-center text-xs font-medium rounded-lg transition-colors ${
                                            currentPage === page
                                                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                                                : 'hover:bg-slate-100 text-slate-600'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                );
                            } else if (
                                page === currentPage - 2 ||
                                page === currentPage + 2
                            ) {
                                return (
                                    <span key={page} className="w-7 h-7 flex items-center justify-center text-xs text-slate-400">
                                        ...
                                    </span>
                                );
                            }
                            return null;
                        })}
                    </div>
                    <button 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Next
                    </button>
                </div>
            </div>
        )}

        {!compact && isCompareOpen && (
          <>
            <div
              className="fixed inset-0 bg-slate-900/40 z-40"
              onClick={() => setIsCompareOpen(false)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center px-2">
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[96vw] max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Compare Trials</h3>
                    <p className="text-xs text-slate-500">{selectedTrials.length} selected</p>
                  </div>
                  <button
                    onClick={() => setIsCompareOpen(false)}
                    className="text-sm font-semibold text-slate-500 hover:text-slate-700"
                  >
                    Close
                  </button>
                </div>
                <div className="overflow-auto">
                  <div className="p-4">
                    <table className="min-w-full text-[11px] border border-slate-100 table-fixed">
                      <colgroup>
                        <col style={{ width: '160px' }} />
                        {selectedTrials.map((trial) => (
                          <col key={`col-${trial.id}`} style={{ width: '360px' }} />
                        ))}
                      </colgroup>
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Attribute</th>
                          {selectedTrials.map((trial) => (
                            <th key={`header-${trial.id}`} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                              {trial.trialName || trial.expDrug || 'Trial'}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {compareAttributes.map((attr) => (
                          <tr key={`compare-row-${attr.key}`} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-600 font-semibold whitespace-pre-wrap break-words break-all">
                              {attr.label}
                            </td>
                            {selectedTrials.map((trial) => (
                              <td
                                key={`compare-${attr.key}-${trial.id}`}
                                className="px-3 py-2 text-slate-700 whitespace-pre-wrap break-words break-all"
                              >
                                {formatCompareValue(attr.get(trial))}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {selectedTrials.length === 0 && (
                          <tr>
                            <td colSpan={1} className="px-4 py-6 text-center text-slate-500">
                              No trials selected.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
    </div>
  );
};

export default TrialTable;
